import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { offset, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { faBell, faClipboardCheck, faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';

import * as Backend from '@spacefill/shared/src/utils/Backend';
import { ChipNumber } from '@spacefill/uikit/src/components/ChipNumber';
import { Dropdown } from '@spacefill/uikit/src/components/Dropdown';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';

import { RequiredActionsList } from './Actions/RequiredActionsList';
import { InAppNotificationsList } from './Notifications/InAppNotificationsList';

const COUNTS_REFRESH_INTERVAL = 5 * 60000;
const ICON_BADGE_CAP = 99;
const PANEL_CHIP_CAP = 99;

function formatCount(value, cap) {
    if (value <= 0 || Number.isNaN(value)) return '';
    return value > cap ? `${cap}+` : String(value);
}

function IconBadge({ value }) {
    if (value <= 0) return null;
    return (
        <div className='absolute -right-1.5 -top-1.5'>
            <div className='inline-flex min-w-4 items-center justify-center rounded-lg px-1 py-0.5 text-[10px] font-semibold bg-red-500 text-white leading-none'>
                {formatCount(value, ICON_BADGE_CAP)}
            </div>
        </div>
    );
}

export function TopBarActionsAndNotifications() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [isTodoTasksOpen, setIsTodoTasksOpen] = useState(false);

    const { data: unreadCount = 0, isFetching: isFetchingNotifications } = useQuery({
        queryKey: ['in-app-notifications/count/unread'],
        queryFn: () => Backend.call('in-app-notifications/count/unread'),
        select: (d) => d?.count ?? 0,
        initialData: 0,
        refetchInterval: COUNTS_REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
    });

    const { data: openTodosCount = 0, isFetching: isFetchingTodoTasks } = useQuery({
        queryKey: ['todo-tasks/count/open'],
        queryFn: () => Backend.call('todo-tasks/count/open'),
        select: (d) => d?.count ?? 0,
        initialData: 0,
        refetchInterval: COUNTS_REFRESH_INTERVAL,
        refetchOnWindowFocus: true,
    });

    const { mutate: readAllMutate } = useMutation({
        mutationFn: () => Backend.call('in-app-notifications/read-all', {}, 'POST'),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: ['in-app-notifications'] });
            queryClient.invalidateQueries({ queryKey: ['in-app-notifications/count/unread'] });
        },
        onError(error) {
            console.error('error during readAll mutation: ', error);
            toast.error(t('Internal error!'));
        },
    });

    const {
        refs: notificationsRefs,
        floatingStyles: notificationsFloatingStyles,
        context: notificationsContext,
    } = useFloating({
        open: isNotificationsOpen,
        onOpenChange: setIsNotificationsOpen,
        placement: 'bottom-end',
        middleware: [offset({ mainAxis: 12 })],
    });

    const notificationsClick = useClick(notificationsContext);
    const notificationsDismiss = useDismiss(notificationsContext);
    const { getReferenceProps: getNotificationsReferenceProps, getFloatingProps: getNotificationsFloatingProps } =
        useInteractions([notificationsClick, notificationsDismiss]);

    const {
        refs: todosRefs,
        floatingStyles: todosFloatingStyles,
        context: todosContext,
    } = useFloating({
        open: isTodoTasksOpen,
        onOpenChange: setIsTodoTasksOpen,
        placement: 'bottom-end',
        middleware: [offset({ mainAxis: 12 })],
    });

    const todosClick = useClick(todosContext);
    const todosDismiss = useDismiss(todosContext);
    const { getReferenceProps: getTodosReferenceProps, getFloatingProps: getTodosFloatingProps } = useInteractions([
        todosClick,
        todosDismiss,
    ]);

    const markAllAsReadButton = (
        <Dropdown
            small
            anchor='bottom end'
            iconEnd={
                <FontAwesomeIcon
                    icon={faEllipsisVertical}
                    className='h-5 text-gray-500 px-1 py-[1px]'
                />
            }
            options={[
                {
                    content: <>{t('Mark all as read')}</>,
                    onClick: () => readAllMutate(),
                    disabled: unreadCount === 0,
                },
            ]}
        />
    );

    return (
        <div className='flex gap-2'>
            <div className='relative'>
                <button
                    ref={todosRefs.setReference}
                    {...getTodosReferenceProps()}
                    className={clsx(
                        'relative flex h-8 w-8 items-center justify-center text-gray-500 transition-all hover:cursor-pointer hover:text-gray-700',
                        {
                            'text-gray-700': isTodoTasksOpen,
                        }
                    )}
                >
                    <FontAwesomeIcon
                        icon={faClipboardCheck}
                        className='text-xl'
                    />
                    {!isFetchingTodoTasks && <IconBadge value={openTodosCount} />}
                </button>

                {isTodoTasksOpen && (
                    <div
                        ref={todosRefs.setFloating}
                        style={todosFloatingStyles}
                        {...getTodosFloatingProps()}
                        className='z-50 w-[400px] max-h-[80vh] rounded-lg bg-white shadow-lg border border-gray-200 flex flex-col'
                    >
                        <div className='flex items-center justify-between border-b border-gray-200 px-4 py-3'>
                            <div className='flex items-center gap-2'>
                                <FontAwesomeIcon
                                    icon={faClipboardCheck}
                                    className='text-gray-500'
                                />
                                <span className='font-semibold text-gray-900'>{t('Required actions')}</span>
                                {openTodosCount > 0 && (
                                    <ChipNumber
                                        variant='light'
                                        value={formatCount(openTodosCount, PANEL_CHIP_CAP)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className='overflow-y-auto px-4 pt-4'>
                            <RequiredActionsList openTodosCount={openTodosCount} />
                        </div>
                    </div>
                )}
            </div>

            <div className='relative'>
                <button
                    ref={notificationsRefs.setReference}
                    {...getNotificationsReferenceProps()}
                    className={clsx(
                        'relative flex h-8 w-8 items-center justify-center text-gray-500 transition-all hover:cursor-pointer hover:text-gray-700',
                        {
                            'text-gray-700': isNotificationsOpen,
                        }
                    )}
                >
                    <FontAwesomeIcon
                        icon={faBell}
                        className='text-xl'
                    />
                    {!isFetchingNotifications && <IconBadge value={unreadCount} />}
                </button>

                {isNotificationsOpen && (
                    <div
                        ref={notificationsRefs.setFloating}
                        style={notificationsFloatingStyles}
                        {...getNotificationsFloatingProps()}
                        className='z-50 w-[400px] max-h-[80vh] rounded-lg bg-white shadow-lg border border-gray-200 flex flex-col'
                    >
                        <div className='flex items-center justify-between border-b border-gray-200 px-4 py-3'>
                            <div className='flex items-center gap-2'>
                                <FontAwesomeIcon
                                    icon={faBell}
                                    className='text-gray-500'
                                />
                                <span className='font-semibold text-gray-900'>{t('Notifications')}</span>
                                {unreadCount > 0 && (
                                    <ChipNumber
                                        variant='light'
                                        value={formatCount(unreadCount, PANEL_CHIP_CAP)}
                                    />
                                )}
                            </div>
                            {unreadCount > 0 && markAllAsReadButton}
                        </div>
                        <div className='overflow-y-auto px-4 pt-4'>
                            <InAppNotificationsList unreadCount={unreadCount} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
